/**
 * Everything that happened on the case, newest first: today's flow as the
 * activity feed tells it, and the workbench's own events — the checklist,
 * the files, the evidence, the asks, every agent run with what it cost, the
 * Assistant's turns. A row about a checklist item, a record, an ask, a run
 * or a turn opens it; the file an ask was answered with opens from its row.
 */

import { useState } from 'react';

import { Badge, Loader, SegmentedControl, Stack, Text } from '@mantine/core';
import { IconPaperclip } from '@tabler/icons-react';

import { ActivityEventRow } from '@/components/Inbox/ActivityFeed';
import type { WorkbenchActivity, WorkbenchActivityEvent } from '@/types/workbench';
import { openWorkbenchAttachment } from './FilesView';
import { formatUsd } from './format';

type Source = 'all' | 'workbench' | 'classic';

/** What a timeline row opens. */
export type ActivityTarget = {
  type: 'checklist' | 'evidence' | 'intervention' | 'task' | 'turn';
  id: string;
};

interface ActivityViewProps {
  reportId: string;
  familyKey: string;
  activity: WorkbenchActivity | null;
  error: Error | null;
  onOpen: (target: ActivityTarget) => void;
}

export function activityTarget(event: WorkbenchActivityEvent): ActivityTarget | null {
  if (!event.entityId) {
    return null;
  }
  switch (event.entityType) {
    case 'checklist_item':
      return { type: 'checklist', id: event.entityId };
    case 'evidence_item':
      return { type: 'evidence', id: event.entityId };
    case 'intervention':
      return { type: 'intervention', id: event.entityId };
    case 'agent_task':
      return { type: 'task', id: event.entityId };
    case 'copilot_message':
      return { type: 'turn', id: event.entityId };
    default:
      return null;
  }
}

export function ActivityView({
  reportId,
  familyKey,
  activity,
  error,
  onOpen,
}: Readonly<ActivityViewProps>) {
  const [source, setSource] = useState<Source>('all');

  if (error && !activity) {
    return (
      <Text size="sm" c="dimmed">
        The timeline couldn&apos;t be loaded.
      </Text>
    );
  }
  if (!activity) {
    return <Loader size="sm" />;
  }

  const events = activity.events.filter((e) => source === 'all' || e.source === source);
  const spent = activity.events.reduce((sum, e) => sum + (e.costUsd ? Number(e.costUsd) : 0), 0);

  return (
    <Stack gap="sm" data-testid="activity-view">
      <SegmentedControl
        size="xs"
        value={source}
        onChange={(v) => setSource(v as Source)}
        data={[
          { label: 'Everything', value: 'all' },
          { label: 'Workbench', value: 'workbench' },
          { label: "Today's flow", value: 'classic' },
        ]}
        style={{ alignSelf: 'flex-start' }}
        data-testid="activity-source"
      />
      {spent > 0 && (
        <Text size="xs" c="dimmed">
          Agent runs shown cost {formatUsd(spent)} in all.
        </Text>
      )}
      {events.length === 0 && (
        <Text size="sm" c="dimmed">
          Nothing recorded here yet.
        </Text>
      )}
      <Stack gap={8}>
        {events.map((event) => {
          const opens = activityTarget(event);
          const cost = formatUsd(event.costUsd);
          const file = event.file;
          return (
            <ActivityEventRow
              key={event.id}
              event={event}
              familyKey={familyKey}
              onClick={opens ? () => onOpen(opens) : undefined}
              aside={
                <>
                  {file && (
                    <Badge
                      component="button"
                      type="button"
                      size="xs"
                      variant="light"
                      color="gray"
                      leftSection={<IconPaperclip size={10} />}
                      style={{ flex: 'none', cursor: 'pointer', textTransform: 'none' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        void openWorkbenchAttachment(reportId, file.conversationAttachmentId);
                      }}
                      data-testid={`activity-file-${event.id}`}
                    >
                      {file.filename}
                    </Badge>
                  )}
                  {cost ? (
                    <Badge size="xs" variant="light" color="blue" style={{ flex: 'none' }}>
                      {cost}
                    </Badge>
                  ) : null}
                </>
              }
            />
          );
        })}
      </Stack>
      {activity.truncated && (
        <Text size="xs" c="dimmed">
          Older activity not shown.
        </Text>
      )}
    </Stack>
  );
}
